import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../../commonComponents/translate';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import { JOURNEY_STEP_STATUS } from '../../../../services/utilityFunctions';
import Carousel from '../../../../commonComponents/carousel/Carousel';
import ReactLoader from '../../../../commonComponents/loader/ReactLoader';
import Notifications from '../../../../services/notifications';
import ShopCartContext from '../../../../contextProviders/ShopCartContext';
import StatusLabel from '../../../../commonComponents/StatusLabel';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import SkipStepButton from './components/SkipStepButton';
import ShopProductItem from '../../../shop/shopItems/ShopProductItem';
import AddPackProduct from '../../../shop/shopItems/pack/advancedPackComponents/AddPackProduct';
import { useModal } from '../../../../services/customHooks';

function JourneyContentPurchase(props) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { t, step, slug: journeySlug, onSkipStep } = props;
  const {
    marketProductVariations = [],
    marketProductPacks = [],
    shouldPurchaseAllProducts,
  } = step;
  const [variationList, setVariationList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setShopCart } = useContext(ShopCartContext);

  const { shippingCountry, currency } = loggedUser;
  const getVariationList = () => {
    setIsLoading(true);
    axios
      .get(
        `/api/market/product/available/list?country=${shippingCountry}&currency=${currency}`,
        {
          params: {
            variations: marketProductVariations,
            products: marketProductPacks,
          },
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
        },
      )
      .then(({ data: response }) => {
        const result = response?.data.filter(o1 =>
          [...marketProductVariations, ...marketProductPacks].some(
            o2 => o1._id === o2,
          ),
        );
        setVariationList(result);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      });
  };

  useEffect(() => {
    getVariationList();
  }, []);

  const addToCart = (productId = null) => {
    const productsToBuy = productId
      ? variationList?.filter(v => v._id === productId)
      : variationList;

    const advancedPacks = productsToBuy.filter(
      product => product?.packType === 'advanced',
    );

    if (advancedPacks?.length > 0) {
      let error = false;
      advancedPacks.forEach(pack => {
        if (!pack?.packVariations || pack?.packVariations.length === 0) {
          Notifications.showNotificationError(
            t('error'),
            t(
              'advancedPack.error.amountVariationsPerAdvancedPack.custom',
              pack?.amountVariationsPerAdvancedPack,
              pack?.name,
            ),
          );
          error = true;
        }
      });
      if (error) return;
    }

    setShopCart(
      productsToBuy?.map(v => {
        let price =
          v.userPrice && v.userPrice?.total >= 0
            ? v.userPrice.total / 100
            : v.userPrice / 100;
        if (v.userPromotionPrice) {
          price =
            v.userPromotionPrice && v.userPromotionPrice?.total >= 0
              ? v.userPromotionPrice.total / 100
              : v.promotionPrice / 100;
        }
        return {
          isPack: v?.parentProduct?.isPack,
          id: v._id,
          amount: 1,
          va: v.va,
          commissionable: v.commissionable,
          price,
          isAdvancedPack: v?.packType === 'advanced',
          amountVariationsPerAdvancedPack: v?.amountVariationsPerAdvancedPack,
          packVariations:
            v?.packType === 'advanced'
              ? v?.packVariations?.map(variation => ({
                  variation: variation?._id,
                  quantity: variation?.quantity,
                }))
              : undefined,
        };
      }),
    );
    window.location.href = `/shop/checkout?journey=${journeySlug}`;
  };

  /** region Advanced Pack Modal */
  const [advancedPackModal, setAdvancedPackModal] = useModal({
    show: false,
    data: null,
  });
  const handleOpenModal = useCallback(
    product => {
      setAdvancedPackModal({ show: true, data: product });
    },
    [setAdvancedPackModal],
  );
  const handleCloseModal = useCallback(
    packVariations => {
      setAdvancedPackModal({ show: false, data: null });
      if (packVariations?.length > 0 && !shouldPurchaseAllProducts) {
        const product = advancedPackModal?.data ?? {};
        const {
          _id,
          userPrice = {},
          userPromotionPrice,
          promotionPrice,
          isPack,
          va,
          commissionable,
          packType,
          amountVariationsPerAdvancedPack,
        } = product;

        // Calculate the price
        let price =
          userPrice?.total >= 0 ? userPrice.total / 100 : userPrice / 100;

        if (userPromotionPrice) {
          price =
            userPromotionPrice?.total >= 0
              ? userPromotionPrice.total / 100
              : promotionPrice / 100;
        }

        const productForCart = {
          isPack,
          id: _id,
          amount: 1,
          va,
          commissionable,
          price,
          isAdvancedPack: packType === 'advanced',
          amountVariationsPerAdvancedPack,
          packVariations:
            packType === 'advanced'
              ? packVariations?.map(variation => ({
                  variation: variation?._id,
                  quantity: variation?.quantity,
                }))
              : undefined,
        };
        setShopCart([productForCart]);
        window.location.href = `/shop/checkout?journey=${journeySlug}`;
      }

      if (packVariations?.length > 0 && shouldPurchaseAllProducts) {
        setVariationList(prevProductsList =>
          prevProductsList?.map(product => {
            if (
              product?._id === advancedPackModal?.data?._id &&
              product?.packType === 'advanced'
            ) {
              return {
                ...product,
                packVariations,
              };
            }
            return product;
          }),
        );
      }
    },
    [setAdvancedPackModal],
  );
  /** endregion Advanced Pack Modal */

  return (
    <>
      {advancedPackModal?.show && (
        <AddPackProduct
          product={{
            ...advancedPackModal?.data,
            pack: {
              ...advancedPackModal?.data?.pack,
              amountVariationsPerAdvancedPack:
                advancedPackModal?.data?.amountVariationsPerAdvancedPack,
            },
          }}
          t={t}
          user={loggedUser}
          onClose={handleCloseModal}
          selectedVariations={advancedPackModal?.data?.packVariations || []}
        />
      )}
      <div className="m-portlet m-portlet-rounded">
        <div className="m-portlet__body purchase-step">
          <ReactLoader isLoading={isLoading}>
            <div className="purchase-header row">
              <div className="col-lg-9 col-6 h6-container">
                <h6>{t('journey.stepType.purchase.items')}</h6>
                <span>{`(${t(
                  shouldPurchaseAllProducts
                    ? 'journey.stepType.purchase.items.subtitle'
                    : 'journey.stepType.purchase.item.subtitle',
                )})`}</span>
              </div>
              <div className="col-lg-3 col-6 btn-container">
                {onSkipStep && (
                  <div className="skip-wrapper">
                    <SkipStepButton onClick={onSkipStep} />
                  </div>
                )}
                {shouldPurchaseAllProducts ? (
                  <>
                    {step.status !== JOURNEY_STEP_STATUS.DONE ? (
                      <button
                        type="button"
                        onClick={() => addToCart(null)}
                        className="addAllToCart addAllToCart-added"
                      >
                        <i className="la la-shopping-cart" />
                        {t('shop.goToCheckout')}
                      </button>
                    ) : (
                      <div>
                        <StatusLabel t={t} status="done" />
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
            <div className="step-purchase-wrapper">
              <Carousel>
                {variationList
                  ?.sort((a, b) => a?.name?.localeCompare(b.name))
                  ?.map(item => {
                    const { parentProduct } = item;
                    const product = parentProduct?.isPack
                      ? {
                          ...item,
                          ...parentProduct,
                          pack: {
                            ...item.pack,
                            userPrice: item.userPrice,
                            userPromotionPrice: item.userPromotionsPrice,
                          },
                        }
                      : {
                          ...parentProduct,
                          variations: [item],
                        };
                    return (
                      <ShopProductItem
                        product={product}
                        t={t}
                        onBuyNow={
                          !shouldPurchaseAllProducts
                            ? () => addToCart(item._id)
                            : null
                        }
                        onOpenAdvancedPackModal={handleOpenModal}
                        journeySlug={journeySlug}
                      />
                    );
                  })}
              </Carousel>
            </div>
          </ReactLoader>
        </div>
      </div>
    </>
  );
}

JourneyContentPurchase.propTypes = {
  t: PropTypes.func.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  slug: PropTypes.string.isRequired,
  onSkipStep: PropTypes.func,
};

JourneyContentPurchase.defaultProps = {
  onSkipStep: null,
};

export default translate(JourneyContentPurchase);
